import { Box, CircularProgress } from "@mui/material"
import { useParams } from "@tanstack/react-router"
import { type FC, useState } from "react"
import { PRODUCT_QUALITY_LAYOUT_ROUTE_PATH } from "routes/app/product/$productId/edit/_layout/quality/_layout"

import {
  useAssociatedTestTemplatesTable,
  useGetProductQualityInstructions,
  useReorderAssociatedTestTemplate,
} from "../../hooks"
import type { ProductQualityInstructions } from "../../types"
import { ProductOperatorActionFormDialog } from "../ProductOperatorActionFormDialog"
import { ProductPostTestNoteFormDialog } from "../ProductPostTestNoteFormDialog"
import { ProductPreTestNoteFormDialog } from "../ProductPreTestNoteFormDialog"

import { AssociatedTestTemplateOperatorActionSection } from "./AssociatedTestTemplateOperatorActionSection"
import { AssociatedTestTemplatePostTestNoteSections } from "./AssociatedTestTemplatePostTestNoteSections"
import { AssociatedTestTemplatePreTestNoteSection } from "./AssociatedTestTemplatePreTestNoteSection"
import { AssociatedTestTemplatesTableSection } from "./AssociatedTestTemplatesTableSection"

import { useUpdateProduct } from "@/screens/Products/hooks/useUpdateProduct"

enum ProductQualityInstructionsDialogType {
  PreTestNote = "PRE_TEST_NOTE",
  PostTestNote = "POST_TEST_NOTE",
  OperatorAction = "OPERATOR_ACTION",
}

export const AssociatedTestTemplatesContainer: FC = () => {
  const [openedDialog, setOpenedDialog] = useState<ProductQualityInstructionsDialogType | null>(null)
  const params = useParams({ from: PRODUCT_QUALITY_LAYOUT_ROUTE_PATH, strict: true })
  const productId = Number(params.productId)

  const {
    productQualityInstructions,
    isFetching: isFetchingProductQualityInstructions,
    refetch: refetchProductQualityInstructions,
  } = useGetProductQualityInstructions(productId)
  const {
    associatedTestTemplates,
    columns,
    isFetching: isFetchingAssociatedTestTemplates,
    refetch: refetchAssociatedTestTemplates,
  } = useAssociatedTestTemplatesTable(productId)
  const { update: updateProduct, isUpdating: isUpdatingProduct } = useUpdateProduct({
    onSuccess: () => {
      refetchProductQualityInstructions()
    },
  })
  const { handleReorder } = useReorderAssociatedTestTemplate({
    associatedTestTemplates,
    refetch: refetchAssociatedTestTemplates,
  })

  const isNoteSectionVisible = associatedTestTemplates.length > 0 && productQualityInstructions

  const handleDialogClose = () => {
    setOpenedDialog(null)
  }

  const handleProductUpdate = async (input: ProductQualityInstructions) => {
    await updateProduct({
      productId,
      ...input,
    })
    handleDialogClose()
  }

  if (isFetchingProductQualityInstructions || isFetchingAssociatedTestTemplates) {
    return (
      <Box className='flex size-full items-center justify-center'>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <>
      <Box className='flex flex-col gap-y-6'>
        {isNoteSectionVisible && (
          <>
            <AssociatedTestTemplatePreTestNoteSection
              productQualityInstructions={productQualityInstructions}
              onEdit={() => setOpenedDialog(ProductQualityInstructionsDialogType.PreTestNote)}
            />
            <AssociatedTestTemplateOperatorActionSection
              productQualityInstructions={productQualityInstructions}
              onEdit={() => setOpenedDialog(ProductQualityInstructionsDialogType.OperatorAction)}
            />
          </>
        )}
        <AssociatedTestTemplatesTableSection
          productId={productId}
          associatedTestTemplates={associatedTestTemplates}
          columns={columns}
          isFetching={isFetchingAssociatedTestTemplates}
          onRefresh={refetchAssociatedTestTemplates}
          onReorder={handleReorder}
        />
        {isNoteSectionVisible && (
          <AssociatedTestTemplatePostTestNoteSections
            productQualityInstructions={productQualityInstructions}
            onEdit={() => setOpenedDialog(ProductQualityInstructionsDialogType.PostTestNote)}
          />
        )}
      </Box>
      <ProductPreTestNoteFormDialog
        isOpen={openedDialog === ProductQualityInstructionsDialogType.PreTestNote}
        instructions={productQualityInstructions}
        isUpdating={isUpdatingProduct}
        onClose={handleDialogClose}
        onSubmit={handleProductUpdate}
      />
      <ProductOperatorActionFormDialog
        isOpen={openedDialog === ProductQualityInstructionsDialogType.OperatorAction}
        instructions={productQualityInstructions}
        isUpdating={isUpdatingProduct}
        onClose={handleDialogClose}
        onSubmit={handleProductUpdate}
      />
      <ProductPostTestNoteFormDialog
        isOpen={openedDialog === ProductQualityInstructionsDialogType.PostTestNote}
        instructions={productQualityInstructions}
        isUpdating={isUpdatingProduct}
        onClose={handleDialogClose}
        onSubmit={handleProductUpdate}
      />
    </>
  )
}
